import { z } from 'zod'
import { refreshSession } from './auth'
import { API_BASE, API_V2_BASE } from './base'
import { useAuthStore } from '../stores/auth'

export class ApiError extends Error {
  constructor(public status: number, message: string, public requestId?: string) {
    super(message)
  }
}

function handleUnauthorized() {
  useAuthStore.getState().clear()
  if (window.location.pathname !== '/login') window.location.assign('/login')
}

async function requestFrom<T>(
  base: string,
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
  allowRefresh = true,
): Promise<T> {
  const token = useAuthStore.getState().token
  const response = await fetch(`${base}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })
  const requestId = response.headers.get('x-request-id') || undefined
  if (response.status === 401) {
    // 静默续期一次（单飞）后重试；auth 端点自身不参与续期
    if (allowRefresh && token) {
      const newToken = await refreshSession()
      if (newToken) return requestFrom(base, path, schema, init, false)
    }
    handleUnauthorized()
    throw new ApiError(401, '登录状态失效，请重新登录', requestId)
  }
  if (!response.ok) throw new ApiError(response.status, `请求失败（${response.status}）`, requestId)
  return schema.parse(await response.json())
}

async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  return requestFrom(API_BASE, path, schema, init)
}

async function requestV2<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  return requestFrom(API_V2_BASE, path, schema, init)
}

const envelope = <T extends z.ZodTypeAny>(data: T) => z.object({ code: z.number(), message: z.string(), data })

/** 有统一信封的端点 */
export async function requestEnvelope<T>(path: string, dataSchema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const result = await request(path, envelope(dataSchema), init)
  if (result.code !== 0) throw new ApiError(result.code, result.message)
  return result.data
}

/** 直接返回 data 的 v2 端点 */
export async function requestEnvelopeV2<T>(path: string, dataSchema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const result = await requestV2(path, envelope(dataSchema), init)
  if (result.code !== 0) throw new ApiError(result.code, result.message)
  return result.data
}

/** 无信封（裸数组等）端点 */
export async function requestRaw<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  return request(path, schema, init)
}

export async function requestRawV2<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  return requestV2(path, schema, init)
}

/** 简单探测端点可达性（登录页用，不抛错） */
export async function probeHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/health`, { headers: { Accept: 'application/json' } })
    return res.ok
  } catch {
    return false
  }
}
